import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { CartItemId, SessionPricingRequestV2DTO, SessionPricingV2DTO } from 'models/purchaseFlowRevamp'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useAPIV2 } from 'utils/API'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import type { PfStoreState } from 'components/pages/PurchaseFlowRevamp/store/store'
import { buildPricedLines } from 'components/pages/PurchaseFlowRevamp/store/serializePaymentIntent'

import { usePfCatalogue } from './purchaseFlowRevamp.query'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'

enum Endpoints {
  SESSION_PRICING = '/workspace/{workspaceId}/sessions/{sessionId}/pricing',
}

export interface SessionPricingResult {
  data: SessionPricingV2DTO | undefined
  /** The cart item behind each request line, from the SAME build the response answers. */
  owners: (CartItemId | null)[] | undefined
  /** True while the answer on hand belongs to an earlier cart (a refetch is running). */
  isStale: boolean
}

/**
 * React Query hook — the session's cart priced the way the payment intent and placement will charge it
 * (`POST …/sessions/{id}/pricing`).
 *
 * Sends the payment intent's own line list, so the two cannot price different carts. Re-keyed on the
 * lines (codes and counts, never prices), so writing the answer back onto the cart does not refetch.
 * `enabled` is the caller's: only a OneSystem embed needs it today.
 */
export function useSessionPricing(enabled: boolean): SessionPricingResult {
  const api = useAPIV2<Endpoints>()

  const items = usePfStore((s) => s.items)
  const extrasFromInstructions = usePfStore((s) => s.extrasFromInstructions)
  const productConfigs = usePfStore((s) => s.productConfigs)
  const draft = usePfStore((s) => s.draft)

  const ws = usePfSessionWorkspaceId()
  const catalogue = usePfCatalogue().data

  const priced = useMemo(
    () => buildPricedLines({ items, extrasFromInstructions, productConfigs, draft } as unknown as PfStoreState, catalogue),
    [items, extrasFromInstructions, productConfigs, draft, catalogue]
  )
  const sessionId = draft.sessionId
  const lines = priced?.lines ?? null

  const query = useQuery({
    queryKey: [EntityKeys.PF_REVAMP_SESSION_PRICING, QueryType.GET_ONE, { ws, sessionId, lines }],
    queryFn: async (): Promise<SessionPricingV2DTO> => {
      if (!ws || !sessionId || !lines) throw new Error('session pricing called without a cart')
      const body: SessionPricingRequestV2DTO = { products: lines }
      return api.post<SessionPricingV2DTO>(
        Endpoints.SESSION_PRICING,
        { workspaceId: ws, sessionId },
        body,
        false
      )
    },
    enabled: enabled && Boolean(ws && sessionId) && lines !== null,
    staleTime: 60_000,
    retry: 1,
    placeholderData: (prev) => prev,
  })

  return {
    data: query.data,
    owners: priced?.owners,
    isStale: query.isPlaceholderData || query.isFetching,
  }
}
